import { createSelector } from '@ngrx/store';
import { webshopFeature } from './webshop.reducer';
import { environment } from '../../../environments/environment';
import { EnrichedCategory, Language } from './webshop.models';
import { MegaMenuItem } from 'primeng/api';

const currentLanguage = (environment.language as Language) || 'NL';

export const {
  selectWebshopState,
  selectData,
  selectLoading,
  selectError
} = webshopFeature;

export const selectWebshop = createSelector(
  selectData,
  (data) => data?.webshop ?? null
);

export const selectBannerText = createSelector(
  selectWebshop,
  (webshop) => webshop?.bannerText?.find(t => t.language === currentLanguage)?.value || webshop?.bannerText?.[0]?.value || ''
);

export const selectPages = createSelector(
  selectData,
  (data) => data?.pages.map(page => ({
    ...page,
    title: page.titles.find(t => t.language === currentLanguage)?.value || page.titles[0]?.value || '',
    slug: page.slugs.find(s => s.language === currentLanguage)?.value || page.slugs[0]?.value || '',
    content: page.content.find(c => c.language === currentLanguage)?.value || page.content[0]?.value || '',
    seoTitle: page.seoTitles?.find(t => t.language === currentLanguage)?.value || page.seoTitles?.[0]?.value || '',
    seoDescription: page.seoDescriptions?.find(d => d.language === currentLanguage)?.value || page.seoDescriptions?.[0]?.value || '',
    seoKeywords: page.seoKeywords?.find(k => k.language === currentLanguage)?.value || page.seoKeywords?.[0]?.value || '',
    ogTitle: page.ogTitles?.find(t => t.language === currentLanguage)?.value || page.ogTitles?.[0]?.value || '',
    ogDescription: page.ogDescriptions?.find(d => d.language === currentLanguage)?.value || page.ogDescriptions?.[0]?.value || '',
    ogImage: page.ogImages?.find(i => i.language === currentLanguage)?.value || page.ogImages?.[0]?.value || ''
  })) ?? []
);

export const selectCategories = createSelector(
  selectData,
  (data) => {
    const categories = data?.categories.map(category => ({
      ...category,
      name: category.name.find(n => n.language === currentLanguage)?.value || category.name[0]?.value || '',
      slug: category.slug.find(s => s.language === currentLanguage)?.value || category.slug[0]?.value || ''
    })) ?? [];

    return categories.map(category => {
      const enriched = { ...category } as EnrichedCategory;
      if (category.parentId) {
        const parent = categories.find(c => c.id === category.parentId);
        if (parent) {
          enriched.parentCategory = parent as EnrichedCategory;
        }
      }
      return enriched;
    });
  }
);

export const selectAttributes = createSelector(
  selectData,
  (data) => data?.attributes.map(attribute => ({
    ...attribute,
    name: attribute.name.find(n => n.language === currentLanguage)?.value || attribute.name[0]?.value || ''
  })) ?? []
);

export const selectProducts = createSelector(
  selectData,
  selectCategories,
  selectAttributes,
  (data, categories, attributes) => {
    const products = data?.products.map(product => {
      const getAttr = (name: string) => product.attributeValues.find(av => {
        const attr = attributes.find(a => a.id === av.attributeId);
        return attr?.name === name;
      })?.value.find(v => v.language === currentLanguage)?.value;

      return {
        ...product,
        title: getAttr('Title') || 'No Title',
        seoTitle: getAttr('SEO Title'),
        seoDescription: getAttr('SEO Description'),
        seoKeywords: getAttr('Keywords'),
        ogTitle: getAttr('OG Title'),
        ogDescription: getAttr('OG Description'),
        category: categories.find(c => c.id === product.categoryId),
        enrichedAttributes: product.attributeValues.map(av => ({
          ...av,
          name: attributes.find(a => a.id === av.attributeId)?.name || '',
          displayValue: av.value.find(v => v.language === currentLanguage)?.value || av.value[0]?.value || ''
        }))
      };
    }) ?? [];

    return products.sort((a, b) => {
      const codeA = a.enrichedAttributes.find(attr => attr.name === 'Productcode')?.displayValue || '';
      const codeB = b.enrichedAttributes.find(attr => attr.name === 'Productcode')?.displayValue || '';
      return codeB.localeCompare(codeA, undefined, { numeric: true, sensitivity: 'base' });
    });
  }
);

export const selectPageBySlug = (slug: string) => createSelector(
  selectPages,
  (pages) => pages.find(p => p.slug === slug)
);

export const selectCategoryBySlug = (slug: string) => createSelector(
  selectCategories,
  (categories) => categories.find(c => c.slug === slug)
);

export const selectProductById = (id: string | undefined) => createSelector(
  selectProducts,
  (products) => products.find(p => p.id === id)
);

export const selectProductsByCategory = (categoryId: string | undefined) => createSelector(
  selectProducts,
  (products) => {
    if (!categoryId) return products;
    return products.filter(p => p.categoryId === categoryId);
  }
);

export const selectMenuItems = createSelector(
  selectPages,
  selectCategories,
  (pages, categories): MegaMenuItem[] => {
    const pageItems = pages.map(page => ({
      label: page.title,
      routerLink: `/${page.slug}`
    }));

    const menuItems: MegaMenuItem[] = [
      {
        label: 'Home',
        routerLink: '/',
        items: pageItems.length > 0 ? [[
          {
            label: 'Pagina\'s',
            items: pageItems
          }
        ]] : undefined
      }
    ];

    // Get top-level categories
    const rootCategories = categories.filter(c => !c.parentId);

    rootCategories.forEach(rootCat => {
      const subCategories = categories.filter(c => c.parentId === rootCat.id);

      const megaMenuItem: MegaMenuItem = {
        label: rootCat.name,
        routerLink: `/producten/${rootCat.slug}`,
        items: subCategories.length > 0 ? [[
          {
            label: rootCat.name,
            items: subCategories.map(sub => ({
              label: sub.name,
              routerLink: `/producten/${rootCat.slug}/${sub.slug}`
            }))
          }
        ]] : undefined
      };

      menuItems.push(megaMenuItem);
    });

    return menuItems;
  }
);

